import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/react-hooks';

import { DispatchEffect, ForgeDoc } from '@atlassian/forge-ui-types';
import {
  RendererNext,
  WebRuntime,
  makeAvatar,
  makeAvatarStack,
  makeThreeLOPromptForCustomUI,
  makeFrame,
} from '@atlassian/forge-ui/ui';

import { ForgeUIKitRendererProps } from '../../types';
import { ForgeIframeRenderer } from '../ForgeIframeRenderer/ForgeIframeRenderer';

export const ForgeUIKit2Renderer: FC<ForgeUIKitRendererProps> = ({
  app,
  contextIds,
  coreData,
  currentUser,
  entryPoint,
  extensionData,
  components,
  onInitialRender,
  getContextToken,
  bridge,
}) => {
  const [isInitialRender, setInitialRender] = useState(true);
  const [forgeDoc, setForgeDoc] = useState<ForgeDoc | undefined>(undefined);
  const [nativeError, setNativeError] = useState<Error | undefined>(undefined);

  const apolloClient = useApolloClient();

  useEffect(() => {
    if (onInitialRender && isInitialRender && forgeDoc) {
      onInitialRender();
      setInitialRender(false);
    }
  }, [forgeDoc, onInitialRender, isInitialRender, setInitialRender]);

  const dispatch = useCallback(async (_: DispatchEffect) => {}, []);

  const wiredComponents = useMemo(
    () => ({
      // Avatar has been renamed to User
      User: makeAvatar({ client: apolloClient }),
      UserGroup: makeAvatarStack({ client: apolloClient }),
      // `UserPicker` is not supported in Forge UI Kit 2. See https://ecosystem-platform.atlassian.net/browse/EXT-1557
      Frame: makeFrame(({ resource }) => {
        return (
          <ForgeIframeRenderer
            app={{
              ...app,
              properties: {
                ...app.properties,
                resource,
              },
            }}
            currentUser={currentUser}
            coreData={coreData}
            extensionData={extensionData}
            onInitialRender={onInitialRender}
            bridge={bridge}
            contextIds={contextIds}
            getContextToken={getContextToken}
          />
        );
      }),
    }),
    [
      apolloClient,
      app,
      bridge,
      contextIds,
      coreData,
      currentUser,
      extensionData,
      getContextToken,
      onInitialRender,
    ]
  );

  const getComponents = useCallback(
    defaults => ({
      ...defaults,
      ...wiredComponents,
      ...(components || []),
      ThreeLOPrompt: makeThreeLOPromptForCustomUI({
        appName: app.properties.title,
      }),
    }),
    [wiredComponents, components, app.properties.title]
  );

  return (
    <>
      <RendererNext
        forgeDoc={forgeDoc}
        error={nativeError?.message}
        dispatch={dispatch}
        components={getComponents}
        extension={app}
        loading
        isNative
      />
      <WebRuntime
        setForgeDoc={setForgeDoc}
        setError={setNativeError}
        accountId={currentUser?.account_id}
        apolloClient={apolloClient}
        components={getComponents}
        contextIds={contextIds}
        extension={app}
        entryPoint={entryPoint}
        coreData={coreData}
        extensionData={extensionData}
        bridge={bridge}
        getContextToken={getContextToken}
      />
    </>
  );
};
